import { useMemo } from 'preact/hooks'
import { processGift } from '@/components/helpers/gwp'
import { globalState } from '@/components/helpers/utils'
import { Cart } from '../helpers/cart.types'
import { GiftWithPurchaseObject, ProcessedGiftWithPurchaseObject } from '../helpers/gwp.types'

export function useGwp(cart: Cart, freeShippingMinimum: number) {
  return useMemo(() => {
    const freeShippingGift = processGift(
      {
        id: 'free-shipping',
        status: 'active',
        startsAt: null,
        endsAt: null,
        title: 'Free Shipping',
        combinesWith: {
          orderDiscounts: true,
          productDiscounts: true,
          shippingDiscounts: true
        },
        giftName: 'Free Shipping',
        giftType: 'shipping',
        giftIds: [],
        giftHandles: [],
        giftProducts: [],
        discountType: 'free',
        discountValue: 100,
        convertDiscountValue: false,
        minPurchaseType: 'amount',
        minPurchaseValue: freeShippingMinimum / 100,
        convertMinPurchaseValue: false,
        maxPurchaseType: 'none',
        maxPurchaseValue: 0,
        convertMaxPurchaseValue: false,
        conditionalType: 'all',
        conditionalIds: [],
        conditionalHandles: [],
        conditionalTags: [],
        countriesType: 'all',
        countryCodes: [],
        customerType: 'all',
        customerTags: [],
        customerQualifies: true
      } as GiftWithPurchaseObject,
      cart
    )
    const gifts = globalState.gwp.map((gift) => processGift(gift, cart))
    const giftsWithThreshold = globalState.cart.gwp.showFreeShippingInProgressBar
      ? [
          ...gifts.filter((gift) => gift.minPurchaseType !== 'none' && gift.minPurchaseValue > 0),
          freeShippingGift
        ].sort(
          (a: ProcessedGiftWithPurchaseObject, b: ProcessedGiftWithPurchaseObject) =>
            a.minPurchaseValue - b.minPurchaseValue
        )
      : gifts.filter((gift) => gift.minPurchaseType !== 'none' && gift.minPurchaseValue > 0)
    const giftsWithoutThreshold = gifts.filter((gift) => gift.minPurchaseType === 'none' || gift.minPurchaseValue === 0)
    const giftsForProgressBar =
      globalState.cart.gwp.giftsToShowInProgressBar === 'both'
        ? giftsWithThreshold
        : globalState.cart.gwp.giftsToShowInProgressBar === 'amount'
          ? giftsWithThreshold.filter((gift) => gift.minPurchaseType === 'amount')
          : giftsWithThreshold.filter((gift) => gift.minPurchaseType === 'quantity')
    const nextAvailableGiftWithThreshold =
      giftsWithThreshold.find((gift) => gift.cartQualifies && !gift.giftInCart && gift.giftType !== 'shipping') ?? null
    const invalidGiftItems = cart.items.filter((item) => {
      if (!item.properties._bl_gwp) {
        return false
      }
      const discountId = item.properties._bl_gwp
      const matchingGift = gifts.find((gift) => gift.id.split('/').pop() === discountId)
      return (
        !matchingGift || (matchingGift.giftInCart && (!matchingGift.cartQualifies || !matchingGift.customerQualifies))
      )
    })

    let previousGiftQualifies = true
    let previousGiftMinPurchaseAmount = 0
    let previousGiftMinPurchaseQuantity = 0
    const rawProgressPercentage = giftsForProgressBar.reduce((percentage, gift, index) => {
      if (gift.qualifyingAmount < gift.minPurchaseAmount) {
        if (previousGiftQualifies) {
          previousGiftQualifies = false
          const giftProgressPercentage =
            (Math.max(0, gift.qualifyingAmount - previousGiftMinPurchaseAmount) /
              (gift.minPurchaseAmount - previousGiftMinPurchaseAmount) /
              giftsForProgressBar.length) *
            100
          return percentage + giftProgressPercentage
        }
        return percentage
      } else if (gift.qualifyingQuantity < gift.minPurchaseQuantity) {
        if (previousGiftQualifies) {
          previousGiftQualifies = false
          const giftProgressPercentage =
            (Math.max(0, gift.qualifyingQuantity - previousGiftMinPurchaseQuantity) /
              (gift.minPurchaseQuantity - previousGiftMinPurchaseQuantity) /
              giftsForProgressBar.length) *
            100
          return percentage + giftProgressPercentage
        }
        return percentage
      }
      previousGiftQualifies = true
      previousGiftMinPurchaseAmount = gift.minPurchaseAmount
      previousGiftMinPurchaseQuantity = gift.minPurchaseQuantity
      return ((index + 1) / giftsForProgressBar.length) * 100
    }, 0)
    const giftsProgressPercentage =
      rawProgressPercentage >= 100 ? 100 : Math.min(Math.round(rawProgressPercentage), 99)

    return {
      giftsWithThreshold,
      giftsWithoutThreshold,
      giftsForProgressBar,
      giftsProgressPercentage,
      nextAvailableGiftWithThreshold,
      invalidGiftItems
    }
  }, [globalState.gwp, cart, freeShippingMinimum])
}
